import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import VisualFeatureBlock from "@/components/VisualFeatureBlock";
import { useLanguage } from "@/contexts/LanguageContext";

const OperationalExample = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Un exemple concret",
      subtitle: "Voici comment fonctionne notre service dans un cas réel",
      example: {
        title: "L'histoire de Sophie et Léa",
        description: "Sophie a créé une capsule temporelle Legatia pour sa fille Léa lorsqu'elle avait 5 ans. Elle a écrit un message pour les 18 ans de Léa, y a joint une contribution financière symbolique, et a invité les grands-parents à participer. Pendant 13 ans, les messages ont été préservés dans nos centres de données sécurisés et la contribution a été gérée dans une banque européenne agréée ACPR. Le jour du 18ème anniversaire de Léa, notre équipe a organisé la révélation de ce cadeau unique, créant un pont émotionnel entre le passé et le présent. L'argent a grandi avec Léa, et aujourd'hui, elle peut l'utiliser pour ses études ou un autre projet important."
      },
      conclusion: {
        title: "",
        description: ""
      }
    },
    en: {
      title: "A Concrete Example",
      subtitle: "Here's how our service works in a real case",
      example: {
        title: "Sophie and Lea's Story",
        description: "Sophie created a Legatia time capsule for her daughter Lea when she was 5 years old. She wrote a message for Lea's 18th birthday, attached a symbolic financial contribution, and invited the grandparents to participate. For 13 years, the messages were preserved in our secure data centers and the contribution was managed in a regulated European bank. On Lea's 18th birthday, our team organized the revelation of this unique gift, creating an emotional bridge between past and present. The money grew with Lea, and today, she can use it for her studies or another important project."
      },
      conclusion: {
        title: "",
        description: ""
      }
    },
    de: {
      title: "Ein konkretes Beispiel",
      subtitle: "So funktioniert unser Service in einem realen Fall",
      example: {
        title: "Die Geschichte von Sophie und Lea",
        description: "Sophie hat eine Legatia-Zeitkapsel für ihre Tochter Lea erstellt, als sie 5 Jahre alt war. Sie schrieb eine Nachricht für Leas 18. Geburtstag, fügte einen symbolischen finanziellen Beitrag bei und lud die Großeltern zur Teilnahme ein. 13 Jahre lang wurden die Nachrichten in unseren sicheren Rechenzentren aufbewahrt und der Beitrag in einer regulierten europäischen Bank verwaltet. An Leas 18. Geburtstag organisierte unser Team die Enthüllung dieses einzigartigen Geschenks und schuf eine emotionale Brücke zwischen Vergangenheit und Gegenwart. Das Geld ist mit Lea gewachsen, und heute kann sie es für ihr Studium oder ein anderes wichtiges Projekt verwenden."
      },
      conclusion: {
        title: "",
        description: ""
      }
    }
  };

  return (
    <section className="section-padding bg-gradient-to-b from-legatia-green/5 to-white">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="section-title">
            {content[language].title}
          </h2>
          <p className="section-subtitle">
            {content[language].subtitle}
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto mb-16">
          <Card className="overflow-hidden border-legatia-yellow/30">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-5">
                {/* Timeline visualization */}
                <div className="md:col-span-2 bg-legatia-yellow/10 p-6 md:p-8 relative">
                  <div className="absolute top-0 bottom-0 left-8 md:left-1/2 w-1 bg-legatia-orange/30 transform md:-translate-x-1/2"></div>
                  
                  {/* Start point */}
                  <div className="relative mb-16 pl-12 md:pl-0 md:text-center">
                    <div className="absolute left-6 md:left-1/2 w-6 h-6 bg-legatia-orange rounded-full transform -translate-y-1/2 md:-translate-x-1/2"></div>
                    <div className="md:pt-8">
                      <p className="font-bold">{language === 'fr' ? 'Création' : language === 'en' ? 'Creation' : 'Erstellung'}</p>
                      <p className="text-sm text-gray-600">2010</p>
                      <p className="text-sm">{language === 'fr' ? 'Léa a 5 ans' : language === 'en' ? 'Lea is 5 years old' : 'Lea ist 5 Jahre alt'}</p>
                    </div>
                  </div>
                  
                  {/* Middle point */}
                  <div className="relative mb-16 pl-12 md:pl-0 md:text-center">
                    <div className="absolute left-6 md:left-1/2 w-6 h-6 bg-legatia-yellow rounded-full transform -translate-y-1/2 md:-translate-x-1/2"></div>
                    <div className="md:pt-8">
                      <p className="font-bold">{language === 'fr' ? 'Conservation' : language === 'en' ? 'Preservation' : 'Aufbewahrung'}</p>
                      <p className="text-sm text-gray-600">2010 - 2023</p>
                      <p className="text-sm">{language === 'fr' ? '13 années d\'attente' : language === 'en' ? '13 years of waiting' : '13 Jahre Wartezeit'}</p>
                    </div>
                  </div>
                  
                  {/* End point */}
                  <div className="relative pl-12 md:pl-0 md:text-center">
                    <div className="absolute left-6 md:left-1/2 w-6 h-6 bg-legatia-green rounded-full transform -translate-y-1/2 md:-translate-x-1/2"></div>
                    <div className="md:pt-8">
                      <p className="font-bold">{language === 'fr' ? 'Révélation' : language === 'en' ? 'Revelation' : 'Enthüllung'}</p>
                      <p className="text-sm text-gray-600">2023</p>
                      <p className="text-sm">{language === 'fr' ? 'Léa a 18 ans' : language === 'en' ? 'Lea is 18 years old' : 'Lea ist 18 Jahre alt'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Story description */}
                <div className="md:col-span-3 p-6 md:p-8">
                  <h3 className="text-2xl font-bold mb-4">
                    {content[language].example.title}
                  </h3>
                  <p className="text-gray-700">
                    {content[language].example.description}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Removing the VisualFeatureBlock */}
      </div>
    </section>
  );
};

export default OperationalExample;
